import { ClockIcon, MapPinIcon, PhoneIcon } from "lucide-react";
import { Skeleton } from "../../components/ui/skeleton";
import { convertE164ToReadable } from "../../utils/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import { useInitialVisitDetails } from "../../hooks/useInitialVisitDetails";
import { useMemo } from "react";
import { useInitialVisitStore } from "../../stores/initialVisitStore";

export function DetailsSection({ uniqueId }: { uniqueId: string | undefined }) {
  const { data, isLoading, error } = useInitialVisitDetails({
    uniqueId,
    enabled: Boolean(uniqueId)
  });

  return (
    <div className="h-full flex flex-col lg:w-96 w-full">
      <div className="border-b border-border p-6">
        <h2 className="header-4">Choose a provider</h2>
        <ProviderSelect uniqueId={uniqueId} />
      </div>
      <div className="p-6">
        {isLoading || error ? (
          <Skeleton className="h-6 w-full" />
        ) : (
          <p className="text-base font-medium text-muted-foreground">
            {data?.organization.name}
          </p>
        )}
        {isLoading || error ? (
          <Skeleton className="h-7 w-full mt-1" />
        ) : (
          <h1 className="header-2">{data?.appointment.name}</h1>
        )}
        <div className="flex flex-col gap-2 mt-6 text-muted-foreground text-sm font-medium">
          <div className="flex items-center gap-2">
            <ClockIcon aria-label="Duration" className="size-4" />
            {isLoading || error ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <p>{data?.appointment.duration} min</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <PhoneIcon aria-label="Phone Number" className="size-4" />
            {isLoading || error ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <p>
                {convertE164ToReadable(data?.organization.phoneNumber || "")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <MapPinIcon aria-label="Location" className="size-4" />
            {isLoading || error ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <p>{data?.organization.location}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderSelect({ uniqueId }: { uniqueId: string | undefined }) {
  const { selectedProvider, setSelectedProvider, setProviderOptions } =
    useInitialVisitStore();

  const { data } = useInitialVisitDetails({
    uniqueId,
    enabled: Boolean(uniqueId)
  });

  const providerOptions = useMemo(() => {
    const options = [
      { label: "All Providers", value: "all" },
      ...(data?.providers.map(
        (provider: { providerId: string; name: string }) => ({
          label: provider.name,
          value: provider.providerId
        })
      ) || [])
    ];
    setProviderOptions(options);
    return options;
  }, [data, setProviderOptions]);

  return (
    <Select
      value={selectedProvider.value}
      onValueChange={(value) =>
        setSelectedProvider(
          providerOptions.find((option) => option.value === value)
        )
      }
    >
      <SelectTrigger
        aria-label="Select provider"
        className="w-auto max-w-full min-w-48 mt-2"
      >
        <SelectValue placeholder="Select provider" />
      </SelectTrigger>
      <SelectContent>
        {providerOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
